// src/Pages/Shop/Payment.tsx
import React, { useEffect, useMemo, useState } from "react";
import { Card, Col, Container, Row, Tab, Alert, Nav, Spinner } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { Shoporder } from "Components/ShopTopBar";
import { useGetStoreSettingsPublicQuery } from "../../redux/services/store/storeSettingsApi";
import { useCreateStoreCheckoutOrderMutation } from "../../redux/services/store/storeCheckoutApi";
import {
    useCreateStoreRazorpayOrderMutation,
    useVerifyStoreRazorpayPaymentMutation,
} from "../../redux/services/store/storePaymentApi";
import { clearBuyNowCheckoutDraft } from "../../Common/buyNowCheckout";

declare global {
    interface Window {
        Razorpay?: any;
    }
}

const toNum = (v: any) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
};

const toCleanString = (value: any) => {
    const text = String(value ?? "").trim();
    return text || null;
};

const loadRazorpayScript = (): Promise<boolean> => {
    return new Promise((resolve) => {
        if (window.Razorpay) {
            resolve(true);
            return;
        }

        const existing = document.querySelector('script[src="https://checkout.razorpay.com/v1/checkout.js"]') as HTMLScriptElement | null;
        if (existing) {
            existing.addEventListener("load", () => resolve(true));
            existing.addEventListener("error", () => resolve(false));
            return;
        }

        const script = document.createElement("script");
        script.src = "https://checkout.razorpay.com/v1/checkout.js";
        script.async = true;
        script.onload = () => resolve(true);
        script.onerror = () => resolve(false);
        document.body.appendChild(script);
    });
};

type PaymentTabKey = "paypal" | "razorpay" | "cash";

const Payment = () => {
    document.title = "payment | Toner - React FrontEnd";

    const location = useLocation();
    const navigate = useNavigate();
    const state = (location.state as any) || {};

    const storeKey = state?.store_key || "default";

    const {
        data: publicSettings,
        isLoading,
        isFetching,
        isError,
    } = useGetStoreSettingsPublicQuery({ store_key: storeKey });

    const [createCheckoutOrder, { isLoading: isCreatingOrder }] = useCreateStoreCheckoutOrderMutation();
    const [createStoreRazorpayOrder, { isLoading: isCreatingRazorpayOrder }] = useCreateStoreRazorpayOrderMutation();
    const [verifyStoreRazorpayPayment, { isLoading: isVerifyingPayment }] = useVerifyStoreRazorpayPaymentMutation();

    const [actionError, setActionError] = useState<string | null>(null);
    const [actionSuccess, setActionSuccess] = useState<string | null>(null);
    const [placingCod, setPlacingCod] = useState(false);

    const currency =
        state?.shipping_currency ||
        publicSettings?.default_currency_code ||
        "USD";

    const shippingAmount = toNum(state?.shipping_amount);

    const subtotal = useMemo(() => {
        return toNum(state?.subtotal);
    }, [state]);

    const discount = useMemo(() => {
        return toNum(state?.discount);
    }, [state]);

    const total = useMemo(() => {
        const stateTotal = toNum(state?.total);
        if (stateTotal > 0) return stateTotal;
        return subtotal + shippingAmount - discount;
    }, [state, subtotal, shippingAmount, discount]);

    const paypalEnabled = publicSettings?.paypal_enabled === true;
    const razorpayEnabled = publicSettings?.online_payment_enabled === true;
    const codEnabled = publicSettings?.cod_enabled === true;

    const enabledTabs = useMemo<PaymentTabKey[]>(() => {
        const tabs: PaymentTabKey[] = [];
        if (paypalEnabled) tabs.push("paypal");
        if (razorpayEnabled) tabs.push("razorpay");
        if (codEnabled) tabs.push("cash");
        return tabs;
    }, [paypalEnabled, razorpayEnabled, codEnabled]);

    const [activeTab, setActiveTab] = useState<PaymentTabKey | null>(null);

    useEffect(() => {
        if (enabledTabs.length === 0) {
            setActiveTab(null);
            return;
        }

        setActiveTab((prev) => {
            if (prev && enabledTabs.includes(prev)) return prev;
            return enabledTabs[0];
        });
    }, [enabledTabs]);

    const isBusy = isLoading || isFetching;
    const isProcessingPayment =
        isCreatingOrder || isCreatingRazorpayOrder || isVerifyingPayment || placingCod;

    const shippingAddressId = Number(state?.shipping_address_id);
    const billingAddressId = Number(state?.billing_address_id);
    const deliveryTypeId = Number(state?.delivery_type_id);
    const notes = state?.notes ?? null;
    const buyNowCustomizationColor = toCleanString(state?.buy_now_item?.customization_color);
    const buyNowCustomizationNote = toCleanString(state?.buy_now_item?.customization_note);
    const buyNowItem =
        state?.buy_now_item &&
        Number.isFinite(Number(state.buy_now_item.variant_id)) &&
        Number.isFinite(Number(state.buy_now_item.qty))
            ? {
                variant_id: Number(state.buy_now_item.variant_id),
                qty: Math.max(1, Math.round(Number(state.buy_now_item.qty))),
                customization_enabled:
                    Boolean(state.buy_now_item.customization_enabled) ||
                    !!buyNowCustomizationColor ||
                    !!buyNowCustomizationNote,
                customization_color: buyNowCustomizationColor,
                customization_note: buyNowCustomizationNote,
            }
            : null;

    const validateCheckoutPayload = () => {
        if (!Number.isFinite(shippingAddressId) || shippingAddressId <= 0) {
            throw new Error("Missing shipping_address_id for checkout.");
        }
        if (!Number.isFinite(billingAddressId) || billingAddressId <= 0) {
            throw new Error("Missing billing_address_id for checkout.");
        }
        if (!Number.isFinite(deliveryTypeId) || deliveryTypeId <= 0) {
            throw new Error("Missing delivery_type_id for checkout.");
        }
    };

    const goToConfirmPage = (args: {
        order_id: number;
        order_no: string;
        payment_status: string;
        payment_method: string;
        fulfillment_status: string;
    }) => {
        navigate("/shop/confirm", {
            replace: true,
            state: {
                order_id: args.order_id,
                order_no: args.order_no,
                payment_status: args.payment_status,
                payment_method: args.payment_method,
                fulfillment_status: args.fulfillment_status,
            },
        });
    };

    const handleRazorpayPayment = async () => {
        try {
            setActionError(null);
            setActionSuccess(null);

            validateCheckoutPayload();

            const scriptLoaded = await loadRazorpayScript();
            if (!scriptLoaded || !window.Razorpay) {
                throw new Error("Failed to load Razorpay checkout.");
            }

            const order = await createCheckoutOrder({
                shipping_address_id: shippingAddressId,
                billing_address_id: billingAddressId,
                delivery_type_id: deliveryTypeId,
                notes,
                finalize_cart: false,
                buy_now_item: buyNowItem,
            }).unwrap();

            const razorpayInit = await createStoreRazorpayOrder({
                order_id: order.id,
                method: "razorpay",
            }).unwrap();

            const options = {
                key: razorpayInit.key_id,
                amount: razorpayInit.amount,
                currency: razorpayInit.currency,
                name: publicSettings?.store?.name || "Store",
                description: `Order ${razorpayInit.order_no}`,
                order_id: razorpayInit.razorpay_order_id,
                handler: async (response: any) => {
                    try {
                        await verifyStoreRazorpayPayment({
                            payment_id: razorpayInit.payment.id,
                            razorpay_order_id: response?.razorpay_order_id || razorpayInit.razorpay_order_id,
                            razorpay_payment_id: response?.razorpay_payment_id,
                            razorpay_signature: response?.razorpay_signature,
                        }).unwrap();

                        setActionError(null);
                        setActionSuccess(`Payment successful for order ${razorpayInit.order_no}.`);
                        if (buyNowItem) {
                            clearBuyNowCheckoutDraft();
                        }

                        goToConfirmPage({
                            order_id: order.id,
                            order_no: razorpayInit.order_no,
                            payment_status: "paid",
                            payment_method: "razorpay",
                            fulfillment_status: order.fulfillment_status || "unfulfilled",
                        });
                    } catch (err: any) {
                        setActionError(
                            err?.data?.detail?.message ||
                            err?.data?.detail ||
                            "Payment verification failed."
                        );
                    }
                },
                prefill: {
                    name: state?.customer_name || "",
                    email: state?.customer_email || "",
                    contact: state?.customer_phone || "",
                },
                notes: {
                    store_order_id: String(order.id),
                    store_order_no: razorpayInit.order_no,
                },
                theme: {
                    color: publicSettings?.primary_color || "#3399cc",
                },
                modal: {
                    ondismiss: () => {
                        setActionError("Razorpay checkout was closed before payment completion.");
                    },
                },
            };

            const razorpay = new window.Razorpay(options);
            razorpay.open();
        } catch (err: any) {
            setActionSuccess(null);
            setActionError(
                err?.data?.detail?.message ||
                err?.data?.detail ||
                err?.message ||
                "Unable to start Razorpay payment."
            );
        }
    };

    const handleCodOrder = async () => {
        try {
            setActionError(null);
            setActionSuccess(null);
            setPlacingCod(true);

            validateCheckoutPayload();

            const order = await createCheckoutOrder({
                shipping_address_id: shippingAddressId,
                billing_address_id: billingAddressId,
                delivery_type_id: deliveryTypeId,
                notes,
                finalize_cart: true,
                buy_now_item: buyNowItem,
            }).unwrap();

            setActionSuccess(`Order ${order.order_no} placed successfully with Cash on Delivery.`);
            if (buyNowItem) {
                clearBuyNowCheckoutDraft();
            }

            goToConfirmPage({
                order_id: order.id,
                order_no: order.order_no,
                payment_status: order.payment_status || "pending",
                payment_method: "cash",
                fulfillment_status: order.fulfillment_status || "unfulfilled",
            });
        } catch (err: any) {
            setActionSuccess(null);
            setActionError(
                err?.data?.detail?.message ||
                err?.data?.detail ||
                err?.message ||
                "Unable to place Cash on Delivery order."
            );
        } finally {
            setPlacingCod(false);
        }
    };

    const orderAction = useMemo(() => {
        if (!activeTab) return null;

        if (activeTab === "paypal") {
            return (
                <button type="button" className="btn btn-primary w-100" disabled>
                    <i className="ri-paypal-fill align-bottom me-2"></i>
                    Pay with Paypal
                </button>
            );
        }

        if (activeTab === "razorpay") {
            return (
                <button
                    type="button"
                    className="btn btn-primary w-100"
                    onClick={handleRazorpayPayment}
                    disabled={isProcessingPayment}
                >
                    {isCreatingOrder || isCreatingRazorpayOrder || isVerifyingPayment ? (
                        <>
                            <Spinner size="sm" animation="border" className="me-2" />
                            Processing...
                        </>
                    ) : (
                        <>
                            <i className="ri-secure-payment-fill align-bottom me-2"></i>
                            Pay with Razorpay
                        </>
                    )}
                </button>
            );
        }

        if (activeTab === "cash") {
            return (
                <button
                    type="button"
                    className="btn btn-primary w-100"
                    onClick={handleCodOrder}
                    disabled={isProcessingPayment}
                >
                    {placingCod ? (
                        <>
                            <Spinner size="sm" animation="border" className="me-2" />
                            Placing order...
                        </>
                    ) : (
                        <>
                            <i className="ri-truck-line align-bottom me-2"></i>
                            Continue with Cash on Delivery
                        </>
                    )}
                </button>
            );
        }

        return null;
    }, [
        activeTab,
        isProcessingPayment,
        isCreatingOrder,
        isCreatingRazorpayOrder,
        isVerifyingPayment,
        placingCod,
    ]);

    return (
        <React.Fragment>
            <section className="section pb-4">
                <Container>
                    {!!actionError && (
                        <Row>
                            <Col lg={12}>
                                <Alert variant="danger">{actionError}</Alert>
                            </Col>
                        </Row>
                    )}

                    {!!actionSuccess && (
                        <Row>
                            <Col lg={12}>
                                <Alert variant="success">{actionSuccess}</Alert>
                            </Col>
                        </Row>
                    )}

                    <Row className="product-list">
                        <Col xl={8}>
                            <h5 className="mb-0 flex-grow-1">Payment Selection</h5>

                            {isBusy ? (
                                <Card className="mt-4">
                                    <Card.Body className="text-center py-5">
                                        <Spinner animation="border" />
                                        <div className="mt-3 text-muted">Loading payment methods...</div>
                                    </Card.Body>
                                </Card>
                            ) : isError ? (
                                <Card className="mt-4">
                                    <Card.Body>
                                        <Alert variant="danger" className="mb-0">
                                            Unable to load payment settings.
                                        </Alert>
                                    </Card.Body>
                                </Card>
                            ) : enabledTabs.length === 0 || !activeTab ? (
                                <Card className="mt-4">
                                    <Card.Body>
                                        <Alert variant="warning" className="mb-0">
                                            No payment methods are available for this store right now.
                                        </Alert>
                                    </Card.Body>
                                </Card>
                            ) : (
                                <Tab.Container activeKey={activeTab}>
                                    <Nav
                                        variant="pills"
                                        className="arrow-navtabs nav-success bg-light mb-3 mt-4 nav-justified custom-nav"
                                        as="ul"
                                        role="tablist"
                                    >
                                        {paypalEnabled && (
                                            <Nav.Item as="li">
                                                <Nav.Link
                                                    eventKey="paypal"
                                                    className="py-3"
                                                    onClick={() => setActiveTab("paypal")}
                                                >
                                                    <span className="d-block d-sm-none">
                                                        <i className="ri-paypal-fill align-bottom"></i>
                                                    </span>
                                                    <span className="d-none d-sm-block">
                                                        <i className="ri-paypal-fill align-bottom pe-2"></i> Paypal
                                                    </span>
                                                </Nav.Link>
                                            </Nav.Item>
                                        )}

                                        {razorpayEnabled && (
                                            <Nav.Item as="li">
                                                <Nav.Link
                                                    eventKey="razorpay"
                                                    className="py-3"
                                                    onClick={() => setActiveTab("razorpay")}
                                                >
                                                    <span className="d-block d-sm-none">
                                                        <i className="ri-secure-payment-fill align-bottom"></i>
                                                    </span>
                                                    <span className="d-none d-sm-block">
                                                        <i className="ri-secure-payment-fill align-bottom pe-2"></i> Razorpay
                                                    </span>
                                                </Nav.Link>
                                            </Nav.Item>
                                        )}

                                        {codEnabled && (
                                            <Nav.Item as="li">
                                                <Nav.Link
                                                    eventKey="cash"
                                                    className="py-3"
                                                    onClick={() => setActiveTab("cash")}
                                                >
                                                    <span className="d-block d-sm-none">
                                                        <i className="ri-money-dollar-box-fill align-bottom"></i>
                                                    </span>
                                                    <span className="d-none d-sm-block">
                                                        <i className="ri-money-dollar-box-fill align-bottom pe-2"></i> Cash on Delivery
                                                    </span>
                                                </Nav.Link>
                                            </Nav.Item>
                                        )}
                                    </Nav>

                                    <Tab.Content className="text-muted">
                                        {paypalEnabled && (
                                            <Tab.Pane eventKey="paypal">
                                                <Card>
                                                    <Card.Body>
                                                        <Row className="gy-3">
                                                            <Col md={12}>
                                                                <label htmlFor="paypal-bname" className="form-label">Buyer Name</label>
                                                                <input
                                                                    type="text"
                                                                    className="form-control"
                                                                    id="paypal-bname"
                                                                    placeholder="Enter Name"
                                                                />
                                                            </Col>

                                                            <Col md={6}>
                                                                <label htmlFor="paypal-email" className="form-label">Email Address</label>
                                                                <input
                                                                    type="email"
                                                                    className="form-control"
                                                                    id="paypal-email"
                                                                    placeholder="Enter Email Address"
                                                                />
                                                            </Col>

                                                            <Col md={6}>
                                                                <label htmlFor="paypal-phone" className="form-label">Phone Number</label>
                                                                <input
                                                                    type="text"
                                                                    className="form-control"
                                                                    id="paypal-phone"
                                                                    placeholder="Enter Phone Number"
                                                                />
                                                            </Col>

                                                            <Col md={12}>
                                                                <label htmlFor="paypal-actype" className="form-label">
                                                                    Select your paypal account type
                                                                </label>
                                                                <div className="d-flex gap-4 mt-1">
                                                                    <div className="form-check">
                                                                        <input
                                                                            className="form-check-input"
                                                                            type="radio"
                                                                            name="paypal_account_type"
                                                                            id="paypal-domestic"
                                                                            defaultChecked
                                                                        />
                                                                        <label
                                                                            htmlFor="paypal-domestic"
                                                                            className="form-check-label"
                                                                        >
                                                                            Domestic
                                                                        </label>
                                                                    </div>
                                                                    <div className="form-check">
                                                                        <input
                                                                            className="form-check-input"
                                                                            type="radio"
                                                                            name="paypal_account_type"
                                                                            id="paypal-international"
                                                                        />
                                                                        <label
                                                                            htmlFor="paypal-international"
                                                                            className="form-check-label"
                                                                        >
                                                                            International
                                                                        </label>
                                                                    </div>
                                                                </div>
                                                            </Col>
                                                        </Row>
                                                    </Card.Body>
                                                </Card>
                                            </Tab.Pane>
                                        )}

                                        {razorpayEnabled && (
                                            <Tab.Pane eventKey="razorpay">
                                                <Card>
                                                    <Card.Body>
                                                        <div className="text-center py-3">
                                                            <div className="avatar-md mx-auto mb-4">
                                                                <div className="avatar-title bg-primary-subtle text-primary rounded-circle display-6">
                                                                    <i className="ri-secure-payment-fill"></i>
                                                                </div>
                                                            </div>
                                                            <h5 className="fs-16 mb-3">Pay Online with Razorpay</h5>
                                                            <p className="text-muted mt-3 mb-0 w-75 mx-auto">
                                                                Securely complete your order using Razorpay. Cards, UPI, net banking and more can be supported from the Razorpay checkout flow.
                                                            </p>
                                                        </div>
                                                    </Card.Body>
                                                </Card>
                                            </Tab.Pane>
                                        )}

                                        {codEnabled && (
                                            <Tab.Pane eventKey="cash">
                                                <Card>
                                                    <Card.Body>
                                                        <div className="text-center py-3">
                                                            <div className="avatar-md mx-auto mb-4">
                                                                <div className="avatar-title bg-primary-subtle text-primary rounded-circle display-6">
                                                                    <i className="bi bi-cash"></i>
                                                                </div>
                                                            </div>
                                                            <h5 className="fs-16 mb-3">Cash on Delivery</h5>
                                                            <p className="text-muted mt-3 mb-0 w-75 mx-auto">
                                                                Place your order now and pay at the time of delivery if Cash on Delivery is available for your location.
                                                            </p>
                                                        </div>
                                                    </Card.Body>
                                                </Card>
                                            </Tab.Pane>
                                        )}
                                    </Tab.Content>
                                </Tab.Container>
                            )}
                        </Col>

                        <Col lg={4}>
                            <Shoporder
                                subtotal={subtotal}
                                dic={discount}
                                charge={shippingAmount}
                                total={total}
                                currency={currency}
                                extraContent={!isBusy && !isError && enabledTabs.length > 0 ? orderAction : null}
                            />
                        </Col>
                    </Row>
                </Container>
            </section>
        </React.Fragment>
    );
};

export default Payment;
